import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { 
    get_home_page_data_api, 
    update_home_content_api, 
    manage_service_api, 
    manage_project_api, 
    manage_stat_api, 
    manage_social_link_api,
    manage_featured_release_api,
    manage_singer_api
} from '../controllers/dataController';
import { FaEdit, FaTrash, FaPlus, FaSave, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const ManageContentPage = () => {
    const [activeTab, setActiveTab] = useState('home');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({
        content: {},
        services: [],
        projects: [],
        stats: [],
        social_links: [],
        featured_releases: [],
        singers: []
    });

    const navigate = useNavigate();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const result = await get_home_page_data_api();
            setData(result);
        } catch (error) {
            console.error(error);
            alert("Failed to load content");
        }
        setLoading(false);
    };

    const handleHomeSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // Filter out file inputs if they are empty strings or not files?
            // Since we are using standard inputs, file inputs need special handling if we want to upload.
            // For now assuming text updates primarily or handling files if possible.
            // Note: The current API in dataController sends JSON. For file uploads we need FormData.
            // But update_home_content_api uses axiosInstance which sends JSON by default.
            // If we need file upload, we need to change how we send data.
            // Let's assume text fields for now or handle FormData.
            
            // To support file uploads, we should use FormData
            // But for simplicity in this generated code, we might stick to text fields first 
            // OR wrap it in FormData if there are files.
            
            // Let's try sending as JSON first (which works for text) and see.
            await update_home_content_api(data.content);
            alert("Home content updated!");
            fetchData();
        } catch (error) {
            console.error(error);
            alert("Failed to update home content");
        }
        setLoading(false);
    };

    const handleHomeChange = (e) => {
        const { name, value } = e.target;
        setData(prev => ({
            ...prev,
            content: { ...prev.content, [name]: value }
        }));
    };

    // Generic List Management
    const ListManager = ({ title, items, api, fields, itemKey }) => {
        const [editingItem, setEditingItem] = useState(null);
        const [newItem, setNewItem] = useState(null);

        const handleDelete = async (id) => {
            if(!window.confirm("Are you sure?")) return;
            try {
                await api('DELETE', { id });
                fetchData();
            } catch (error) {
                alert("Failed to delete");
            }
        };

        const handleSave = async (item, isNew) => {
            try {
                const method = isNew ? 'POST' : 'PUT';
                // Handle JSON fields if any (e.g. projects for singers)
                // For now, simple text fields. If 'projects' is a string, try parsing it
                let dataToSend = { ...item };
                if (dataToSend.projects && typeof dataToSend.projects === 'string') {
                    try {
                        dataToSend.projects = JSON.parse(dataToSend.projects);
                    } catch (e) {
                        // ignore or alert
                    }
                }

                // Check for file uploads
                const hasFile = Object.keys(dataToSend).some(key => dataToSend[key] instanceof File);
                
                if (hasFile) {
                    const formData = new FormData();
                    Object.keys(dataToSend).forEach(key => {
                        if (key === 'projects' && typeof dataToSend[key] === 'object') {
                             formData.append(key, JSON.stringify(dataToSend[key]));
                        } else {
                             formData.append(key, dataToSend[key]);
                        }
                    });
                    dataToSend = formData;
                }
                
                await api(method, dataToSend);
                setEditingItem(null);
                setNewItem(null);
                fetchData();
            } catch (error) {
                console.error(error);
                alert("Failed to save");
            }
        };

        return (
            <div className="bg-card p-6 rounded-xl shadow-sm border border-border/50">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold text-primary">{title}</h3>
                    <button onClick={() => setNewItem({})} className="bg-primary text-primary-foreground px-4 py-2 rounded flex items-center gap-2">
                        <FaPlus /> Add New
                    </button>
                </div>

                {newItem && (
                    <div className="mb-6 p-4 border border-primary/20 rounded bg-primary/5">
                        <h4 className="font-bold mb-2">New {title}</h4>
                        <FormFields fields={fields} data={newItem} onChange={(e) => {
                            if (e.target.type === 'file') {
                                setNewItem({...newItem, [e.target.name]: e.target.files[0]})
                            } else {
                                setNewItem({...newItem, [e.target.name]: e.target.value})
                            }
                        }} />
                        <div className="flex gap-2 mt-4">
                            <button onClick={() => handleSave(newItem, true)} className="bg-green-600 text-white px-3 py-1 rounded"><FaSave /> Save</button>
                            <button onClick={() => setNewItem(null)} className="bg-gray-500 text-white px-3 py-1 rounded"><FaTimes /> Cancel</button>
                        </div>
                    </div>
                )}

                <div className="space-y-4">
                    {items && items.map(item => (
                        <div key={item.id} className="border p-4 rounded bg-background/50 flex justify-between items-start">
                            {editingItem?.id === item.id ? (
                                <div className="w-full">
                                    <FormFields fields={fields} data={editingItem} onChange={(e) => {
                                        if (e.target.type === 'file') {
                                            setEditingItem({...editingItem, [e.target.name]: e.target.files[0]})
                                        } else {
                                            setEditingItem({...editingItem, [e.target.name]: e.target.value})
                                        }
                                    }} />
                                    <div className="flex gap-2 mt-4">
                                        <button onClick={() => handleSave(editingItem, false)} className="bg-green-600 text-white px-3 py-1 rounded"><FaSave /> Save</button>
                                        <button onClick={() => setEditingItem(null)} className="bg-gray-500 text-white px-3 py-1 rounded"><FaTimes /> Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-2 gap-4 w-full mr-4">
                                        {fields.map(field => (
                                            <div key={field.name}>
                                                <span className="font-semibold text-xs text-muted-foreground uppercase">{field.label}:</span>
                                                <div className="truncate">
                                                    {field.type === 'file' && item[field.name] ? (
                                                        <img src={item[field.name]} alt="preview" className="h-10 w-10 object-cover rounded" />
                                                    ) : field.type === 'list' && item[field.name] ? (
                                                       item[field.name].length  
                                                    ) :(
                                                        typeof item[field.name] === 'object' ? JSON.stringify(item[field.name]) : item[field.name]
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex gap-2 min-w-fit">
                                        <button onClick={() => setEditingItem(item)} className="text-blue-500 hover:text-blue-700"><FaEdit size={18} /></button>
                                        <button onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700"><FaTrash size={18} /></button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const FormFields = ({ fields, data, onChange }) => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map(field => {
                let value = data[field.name] || '';
                if (typeof value === 'object' && field.type !== 'file' && field.type !== 'list') {
                    value = JSON.stringify(value);
                }

                if (field.type === 'list') {
                    const listItems = Array.isArray(data[field.name]) ? data[field.name] : [];
                    return (
                        <div key={field.name} className="flex flex-col col-span-2 border p-4 rounded bg-background/30">
                            <label className="text-sm font-medium mb-2">{field.label}</label>
                            {listItems.map((item, index) => (
                                <div key={index} className="mb-4 p-3 border border-border/50 rounded flex flex-col gap-2 relative">
                                    <button 
                                        type="button"
                                        onClick={() => {
                                            const newList = [...listItems];
                                            newList.splice(index, 1);
                                            onChange({ target: { name: field.name, value: newList } });
                                        }}
                                        className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                                    >
                                        <FaTimes />
                                    </button>
                                    <input 
                                        placeholder="Title" 
                                        value={item.title || ''} 
                                        onChange={(e) => {
                                            const newList = [...listItems];
                                            newList[index] = { ...item, title: e.target.value };
                                            onChange({ target: { name: field.name, value: newList } });
                                        }}
                                        className="bg-background border border-border rounded p-2 text-foreground text-sm"
                                    />
                                    <input 
                                        placeholder="Video URL" 
                                        value={item.videoUrl || ''} 
                                        onChange={(e) => {
                                            const newList = [...listItems];
                                            newList[index] = { ...item, videoUrl: e.target.value };
                                            onChange({ target: { name: field.name, value: newList } });
                                        }}
                                        className="bg-background border border-border rounded p-2 text-foreground text-sm"
                                    />
                                    <input 
                                        placeholder="Year" 
                                        value={item.year || ''} 
                                        onChange={(e) => {
                                            const newList = [...listItems];
                                            newList[index] = { ...item, year: e.target.value };
                                            onChange({ target: { name: field.name, value: newList } });
                                        }}
                                        className="bg-background border border-border rounded p-2 text-foreground text-sm"
                                    />
                                    {/* Simplified thumbnail handling: URL text input for now or file if backend supported nested file uploads easily. 
                                        For simplicity let's assume URL or we can add file logic later. The requirement didn't specify nested file uploads explicitly but existing structure uses URL. 
                                        Let's stick to URL/Text for thumbnail in this list for now to keep it robust. */}
                                    <input 
                                        placeholder="Thumbnail URL" 
                                        value={item.thumbnail || ''} 
                                        onChange={(e) => {
                                            const newList = [...listItems];
                                            newList[index] = { ...item, thumbnail: e.target.value };
                                            onChange({ target: { name: field.name, value: newList } });
                                        }}
                                        className="bg-background border border-border rounded p-2 text-foreground text-sm"
                                    />
                                </div>
                            ))}
                            <button 
                                type="button"
                                onClick={() => {
                                    const newList = [...listItems, { id: Date.now().toString(), title: '', videoUrl: '', year: '', thumbnail: '' }];
                                    onChange({ target: { name: field.name, value: newList } });
                                }}
                                className="bg-secondary text-secondary-foreground px-3 py-1 rounded text-sm w-fit flex items-center gap-2 mt-2"
                            >
                                <FaPlus size={12} /> Add Item
                            </button>
                        </div>
                    );
                }

                return (
                <div key={field.name} className="flex flex-col">
                    <label className="text-sm font-medium mb-1">{field.label}</label>
                    {field.type === 'textarea' ? (
                        <textarea 
                            name={field.name} 
                            value={value} 
                            onChange={onChange}
                            className="bg-background border border-border rounded p-2 text-foreground"
                            rows={3}
                        />
                    ) : field.type === 'file' ? (
                        <div className="flex gap-2">
                        {value && <img src={value} alt="preview" className="h-10 w-10 object-cover rounded" />}

                        <input 
                            type="file" 
                            name={field.name} 
                            onChange={onChange}
                            className="bg-background border border-border rounded p-2 text-foreground"
                        />
                        </div>
                    ) : (
                        <input 
                            type={field.type || 'text'} 
                            name={field.name} 
                            value={value} 
                            onChange={onChange}
                            className="bg-background border border-border rounded p-2 text-foreground"
                        />
                    )}
                </div>
            )})}
        </div>
    );

    return (
        <div className="bg-background min-h-screen relative">
            <Header />
            <div className="pt-28 pb-10 px-4 max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-bold text-foreground">Manage Website Content</h1>
                </div>

                {/* Tabs */}
                <div className="flex space-x-2 mb-8 overflow-x-auto pb-2">
                    {['home', 'featured', 'singers', 'services', 'projects', 'stats', 'links'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-4 py-2 rounded-lg font-medium capitalize whitespace-nowrap ${
                                activeTab === tab 
                                ? 'bg-primary text-primary-foreground' 
                                : 'bg-card text-muted-foreground hover:bg-card/80'
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                {loading && <div className="text-center py-10">Loading...</div>}

                {!loading && (
                    <>
                        {activeTab === 'home' && (
                            <div className="bg-card p-6 rounded-xl shadow-sm border border-border/50">
                                <h2 className="text-xl font-semibold mb-6 text-primary">Home Page Content</h2>
                                <form onSubmit={handleHomeSubmit} className="space-y-6">
                                    <div className="grid grid-cols-1 gap-6">
                                        <h3 className="text-lg font-medium border-b pb-2">Hero Section</h3>
                                        <FormFields fields={[
                                            { name: 'hero_title', label: 'Hero Title' },
                                            { name: 'hero_subtitle', label: 'Hero Subtitle' },
                                            { name: 'hero_description', label: 'Hero Description', type: 'textarea' },
                                        ]} data={data.content} onChange={handleHomeChange} />

                                        <h3 className="text-lg font-medium border-b pb-2 mt-4">Featured Video</h3>
                                        <FormFields fields={[
                                            { name: 'featured_video_title', label: 'Video Title' },
                                            { name: 'featured_video_subtitle', label: 'Video Subtitle' },
                                            { name: 'featured_video_id', label: 'YouTube Video ID' },
                                            { name: 'featured_video_description', label: 'Description', type: 'textarea' },
                                        ]} data={data.content} onChange={handleHomeChange} />

                                        <h3 className="text-lg font-medium border-b pb-2 mt-4">About Section</h3>
                                        <FormFields fields={[
                                            { name: 'about_title', label: 'About Title' },
                                            { name: 'about_description_1', label: 'Description 1', type: 'textarea' },
                                            { name: 'about_description_2', label: 'Description 2', type: 'textarea' },
                                        ]} data={data.content} onChange={handleHomeChange} />

                                        <h3 className="text-lg font-medium border-b pb-2 mt-4">Contact Info</h3>
                                        <FormFields fields={[
                                            { name: 'contact_email', label: 'Email' },
                                            { name: 'contact_phone', label: 'Phone' },
                                            { name: 'contact_address', label: 'Address' },
                                        ]} data={data.content} onChange={handleHomeChange} />
                                    </div>
                                    <button type="submit" className="bg-primary text-primary-foreground px-6 py-2 rounded-lg font-bold flex items-center gap-2">
                                        <FaSave /> Save Changes
                                    </button>
                                </form>
                            </div>
                        )}

                        {activeTab === 'featured' && (
                            <ListManager 
                                title="Featured Releases" 
                                items={data.featured_releases} 
                                api={manage_featured_release_api}
                                fields={[
                                    { name: 'title', label: 'Title' },
                                    { name: 'subtitle', label: 'Subtitle' },
                                    { name: 'category', label: 'Category' },
                                    { name: 'release_date', label: 'Release Date' },
                                    { name: 'youtube_link', label: 'YouTube Link' },
                                    { name: 'image', label: 'Image', type: 'file' },
                                    { name: 'order', label: 'Order', type: 'number' },
                                ]}
                            />
                        )}

                        {activeTab === 'singers' && (
                            <ListManager 
                                title="Singers & Collaborations" 
                                items={data.singers} 
                                api={manage_singer_api}
                                fields={[
                                    { name: 'name', label: 'Name' },
                                    { name: 'genre', label: 'Genre' },
                                    { name: 'image', label: 'Image', type: 'file' },
                                    { name: 'projects', label: 'Projects', type: 'list' },
                                    { name: 'order', label: 'Order', type: 'number' },
                                ]}
                            />
                        )}

                        {activeTab === 'services' && (
                            <ListManager 
                                title="Services" 
                                items={data.services} 
                                api={manage_service_api}
                                fields={[
                                    { name: 'title', label: 'Title' },
                                    { name: 'description', label: 'Description', type: 'textarea' },
                                    { name: 'icon_name', label: 'Icon Name (Lucide)' },
                                    { name: 'order', label: 'Order', type: 'number' },
                                ]}
                            />
                        )}

                        {activeTab === 'projects' && (
                            <ListManager 
                                title="Projects" 
                                items={data.projects} 
                                api={manage_project_api}
                                fields={[
                                    { name: 'title', label: 'Title' },
                                    { name: 'category', label: 'Category' },
                                    { name: 'youtube_link', label: 'YouTube Link' },
                                    { name: 'image', label: 'Image', type: 'file' },
                                    { name: 'order', label: 'Order', type: 'number' },
                                ]}
                            />
                        )}

                        {activeTab === 'stats' && (
                            <ListManager 
                                title="Statistics" 
                                items={data.stats} 
                                api={manage_stat_api}
                                fields={[
                                    { name: 'label', label: 'Label' },
                                    { name: 'value', label: 'Value', type: 'number' },
                                    { name: 'suffix', label: 'Suffix (e.g. +)' },
                                    { name: 'icon_name', label: 'Icon Name' },
                                    { name: 'order', label: 'Order', type: 'number' },
                                ]}
                            />
                        )}

                        {activeTab === 'links' && (
                            <ListManager 
                                title="Social Links" 
                                items={data.social_links} 
                                api={manage_social_link_api}
                                fields={[
                                    { name: 'platform', label: 'Platform Name' },
                                    { name: 'url', label: 'URL' },
                                    { name: 'icon_name', label: 'Icon Name' },
                                    { name: 'order', label: 'Order', type: 'number' },
                                ]}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ManageContentPage;
